import { useCallback, useEffect, useMemo, useState } from "react";
import {
    collection,
    doc,
    documentId,
    endAt,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    startAfter,
    startAt,
    where,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth, db } from "../../firebase.init";

export const REWARDS_FUNCTION_REGION = "europe-west2";
export const REWARDS_TIME_ZONE = "Europe/London";

const functions = getFunctions(undefined, REWARDS_FUNCTION_REGION);

const memberPath = (uid) => `rewards/users/members/${uid}`;
const memberCollectionPath = (uid, name) => `rewards/users/members/${uid}/${name}`;
const rewardTicketPath = (ticketId) => `rewards/tickets/items/${ticketId}`;

export const makeRequestId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `req_${Date.now()}_${Math.random().toString(36).slice(2, 14)}`;
};

export const callRewardFunction = async (action, payload = {}, includeRequestId = true) => {
    const callable = httpsCallable(functions, "rewardsAction");
    try {
        const response = await callable({
            action,
            payload,
            ...(includeRequestId ? { requestId: makeRequestId() } : {}),
        });
        return response.data;
    } catch (error) {
        const details = error?.details || null;
        const supported = Array.isArray(details?.supportedActions)
            ? ` Supported actions: ${details.supportedActions.join(", ")}.`
            : "";
        const message = `${error?.message?.replace(/^Firebase:\s*/i, "") || "The rewards operation failed."}${supported}`;
        const wrapped = new Error(message);
        wrapped.code = error?.code || "unknown";
        wrapped.details = details;
        throw wrapped;
    }
};

const zonedDateParts = (date, timeZone) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).formatToParts(date);
    return Object.fromEntries(
        parts
            .filter((part) => part.type !== "literal")
            .map((part) => [part.type, Number(part.value)]),
    );
};

export const currentIsoWeekKey = (date = new Date(), timeZone = REWARDS_TIME_ZONE) => {
    const { year, month, day } = zonedDateParts(date, timeZone);
    const utc = new Date(Date.UTC(year, month - 1, day));
    const weekday = utc.getUTCDay() || 7;
    utc.setUTCDate(utc.getUTCDate() + 4 - weekday);
    const weekYear = utc.getUTCFullYear();
    const yearStart = new Date(Date.UTC(weekYear, 0, 1));
    const week = Math.ceil((((utc - yearStart) / 86400000) + 1) / 7);
    return `${weekYear}-W${String(week).padStart(2, "0")}`;
};

export const toDate = (value) => {
    if (!value) return null;
    if (typeof value?.toDate === "function") return value.toDate();
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const formatRewardDate = (value, options = {}) => {
    const date = toDate(value);
    if (!date) return "—";
    return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        ...options,
    });
};

export const formatPoints = (value, { sign = false } = {}) => {
    const points = Number(value || 0);
    if (sign && points > 0) return `+${points}`;
    return String(points);
};

const docsToData = (snapshot) => snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));

const REWARD_ASSIGNABLE_ROLES = ["admin", "inspection", "coordinator", "reviewer", "remedial", "manager"];

const QUALITY_REWARD_POINTS = Object.freeze({
    difficult_task: 5,
    urgent_management_request: 10,
});
const QUALITY_REWARD_TYPES = new Set(Object.keys(QUALITY_REWARD_POINTS));
const AUTO_CONFIRM_REWARD_TYPES = new Set(["speed_bonus"]);
const HISTORY_PAGE_SIZE = 100;
const normalizeRewardRole = (role) => role === "coordinator2" ? "coordinator" : String(role || "").trim().toLowerCase();

const loadRewardDashboard = async (userId, { includeAdmin = true } = {}) => {
    const callerUid = auth.currentUser?.uid;
    if (!callerUid) throw new Error("You need to be signed in to view rewards.");

    const requestedUid = userId || callerUid;
    const weekKey = currentIsoWeekKey();
    const initialSnapshots = await Promise.all([
        getDoc(doc(db, "users", callerUid)),
        requestedUid === callerUid ? Promise.resolve(null) : getDoc(doc(db, "users", requestedUid)),
        getDoc(doc(db, memberPath(requestedUid))),
        getDoc(doc(db, memberCollectionPath(requestedUid, "periods"), weekKey)),
    ]);
    const callerSnap = initialSnapshots[0];
    const userSnap = initialSnapshots[1];
    let summarySnap = initialSnapshots[2];
    let periodSnap = initialSnapshots[3];

    const callerProfile = callerSnap.exists() ? callerSnap.data() : {};
    const callerRole = normalizeRewardRole(callerProfile.user_role);
    const canViewOther = callerRole === "admin";
    if (requestedUid !== callerUid && !canViewOther) {
        throw new Error("You cannot view another employee's rewards.");
    }

    const requestedProfile = requestedUid === callerUid
        ? callerProfile
        : (userSnap?.exists() ? userSnap.data() : {});

    let [ledgerSnap, achievementSnap, catalogSnap, redemptionSnap] = await Promise.all([
        getDocs(query(collection(db, memberCollectionPath(requestedUid, "ledger")), orderBy("created_at", "desc"), limit(HISTORY_PAGE_SIZE))),
        getDocs(query(collection(db, memberCollectionPath(requestedUid, "achievements")), orderBy("awarded_at", "desc"), limit(100))),
        getDocs(query(collection(db, "rewards/catalog/items"), orderBy("sort_order", "asc"), limit(100))),
        getDocs(query(collection(db, memberCollectionPath(requestedUid, "redemptions")), orderBy("requested_at", "desc"), limit(50))),
    ]);

    const hasLegacyAutomaticPending = ledgerSnap.docs.some((snapshot) => {
        const entry = snapshot.data() || {};
        return entry.status === "pending" && AUTO_CONFIRM_REWARD_TYPES.has(entry.type);
    });
    if (hasLegacyAutomaticPending) {
        try {
            await callRewardFunction("reconcileAutomaticRewards", { userId: requestedUid }, false);
            [summarySnap, periodSnap, ledgerSnap] = await Promise.all([
                getDoc(doc(db, memberPath(requestedUid))),
                getDoc(doc(db, memberCollectionPath(requestedUid, "periods"), weekKey)),
                getDocs(query(collection(db, memberCollectionPath(requestedUid, "ledger")), orderBy("created_at", "desc"), limit(HISTORY_PAGE_SIZE))),
            ]);
        } catch (error) {
            console.warn("Could not reconcile legacy automatic rewards yet:", error);
        }
    }

    let pendingRedemptions = [];
    let pendingQuality = [];
    let recentIncidents = [];
    let recentAwards = [];
    let recentAudit = [];
    let teamUsers = [];

    if (callerRole === "admin" && includeAdmin) {
        const [adminRedemptions, adminQuality, incidents, awards, audit, team, memberSummaries] = await Promise.all([
            getDocs(query(collection(db, "rewards/admin/redemptions"), orderBy("requested_at", "desc"), limit(150))),
            getDocs(query(collection(db, "rewards/admin/quality"), orderBy("created_at", "desc"), limit(200))),
            getDocs(query(collection(db, "rewards/admin/incidents"), orderBy("occurred_at", "desc"), limit(50))),
            getDocs(query(collection(db, "rewards/admin/awards"), orderBy("approved_at", "desc"), limit(50))),
            getDocs(query(collection(db, "rewards/admin/audit"), orderBy("occurred_at", "desc"), limit(200))),
            getDocs(collection(db, "users")),
            getDocs(collection(db, "rewards/users/members")),
        ]);

        const summaryByUser = new Map(docsToData(memberSummaries).map((item) => [item.id, item]));
        teamUsers = docsToData(team)
            .map((employee) => ({
                ...employee,
                user_role: normalizeRewardRole(employee.user_role),
                reward_summary: summaryByUser.get(employee.id) || {},
            }))
            .filter((employee) => REWARD_ASSIGNABLE_ROLES.includes(employee.user_role) && employee.rewards_disabled !== true)
            .sort((a, b) => String(a.name || a.email || a.id).localeCompare(String(b.name || b.email || b.id)));
        const teamById = new Map(teamUsers.map((employee) => [employee.id, employee]));
        const displayName = (uid) => teamById.get(uid)?.name || teamById.get(uid)?.email || uid || "Unknown employee";

        pendingRedemptions = docsToData(adminRedemptions)
            .filter((item) => item.status === "pending")
            .map((item) => ({ ...item, user_name: displayName(item.user_id) }));
        const pendingQualityBase = docsToData(adminQuality)
            .filter((item) => item.status === "pending");
        pendingQuality = pendingQualityBase.map((item) => {
            const rawDetails = (item.reward_details || []).length
                ? item.reward_details
                : (item.reward_types || []).map((type) => ({ type }));
            const rewardDetails = rawDetails
                .filter((entry) => QUALITY_REWARD_TYPES.has(entry.type))
                .map((entry) => ({
                    ...entry,
                    points: Number(entry.points || QUALITY_REWARD_POINTS[entry.type] || 0),
                }));
            return {
                ...item,
                user_name: displayName(item.user_id),
                reward_types: rewardDetails.map((entry) => entry.type),
                reward_details: rewardDetails,
                pending_points: rewardDetails.reduce((sum, entry) => sum + Number(entry.points || 0), 0),
            };
        }).filter((item) => item.reward_details.length > 0);
        recentIncidents = docsToData(incidents)
            .map((item) => ({ ...item, user_name: displayName(item.user_id) }));
        recentAwards = docsToData(awards)
            .map((item) => ({ ...item, user_name: displayName(item.user_id) }));
        recentAudit = docsToData(audit)
            .map((item) => ({ ...item, user_name: displayName(item.user_id), reviewer_name: displayName(item.reviewed_by) }));
    }

    const summaryData = summarySnap.exists() ? summarySnap.data() : {};
    const confirmed = Number(summaryData.confirmed_points || 0);
    const reserved = Number(summaryData.reserved_points || 0);
    const summary = {
        confirmed_points: confirmed,
        pending_points: Number(summaryData.pending_points || 0),
        reserved_points: reserved,
        available_points: confirmed - reserved,
        ...summaryData,
    };

    return {
        user: { id: requestedUid, ...requestedProfile },
        viewer: { id: callerUid, role: callerRole || "" },
        summary,
        current_period: {
            period_key: weekKey,
            confirmed_points: 0,
            pending_points: 0,
            ...(periodSnap.exists() ? periodSnap.data() : {}),
        },
        history: docsToData(ledgerSnap),
        history_cursor: ledgerSnap.docs[ledgerSnap.docs.length - 1] || null,
        history_has_more: ledgerSnap.size === HISTORY_PAGE_SIZE,
        achievements: docsToData(achievementSnap),
        catalog: docsToData(catalogSnap).filter((item) => item.active !== false),
        redemptions: docsToData(redemptionSnap),
        pending_redemptions: pendingRedemptions,
        pending_quality: pendingQuality,
        recent_incidents: recentIncidents,
        recent_awards: recentAwards,
        recent_audit: recentAudit,
        team_users: teamUsers,
        policy: {
            speed_threshold_seconds: 600,
            time_zone: REWARDS_TIME_ZONE,
        },
    };
};

export const useLiveRewardSummary = (userId) => {
    const weekKey = useMemo(() => currentIsoWeekKey(), []);
    const [summary, setSummary] = useState({
        confirmed_points: 0,
        pending_points: 0,
        reserved_points: 0,
        available_points: 0,
    });
    const [period, setPeriod] = useState({ period_key: weekKey, confirmed_points: 0, pending_points: 0 });
    const [loading, setLoading] = useState(Boolean(userId));

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return undefined;
        }
        setLoading(true);
        let summaryReady = false;
        let periodReady = false;
        const publishLoading = () => setLoading(!(summaryReady && periodReady));
        const unsubscribeSummary = onSnapshot(
            doc(db, memberPath(userId)),
            (snapshot) => {
                const value = snapshot.exists() ? snapshot.data() : {};
                const confirmed = Number(value.confirmed_points || 0);
                const reserved = Number(value.reserved_points || 0);
                setSummary({
                    confirmed_points: confirmed,
                    pending_points: Number(value.pending_points || 0),
                    reserved_points: reserved,
                    available_points: confirmed - reserved,
                    ...value,
                });
                summaryReady = true;
                publishLoading();
            },
            () => {
                summaryReady = true;
                publishLoading();
            },
        );
        const unsubscribePeriod = onSnapshot(
            doc(db, memberCollectionPath(userId, "periods"), weekKey),
            (snapshot) => {
                setPeriod({ period_key: weekKey, confirmed_points: 0, pending_points: 0, ...(snapshot.exists() ? snapshot.data() : {}) });
                periodReady = true;
                publishLoading();
            },
            () => {
                periodReady = true;
                publishLoading();
            },
        );
        return () => {
            unsubscribeSummary();
            unsubscribePeriod();
        };
    }, [userId, weekKey]);

    return { summary, period, loading, weekKey };
};

const watchRewardDashboard = async (userId, onChange, onError, { includeAdmin = true } = {}) => {
    const callerUid = auth.currentUser?.uid;
    if (!callerUid || !userId) return () => { };

    const requestedUid = userId || callerUid;
    const callerSnap = await getDoc(doc(db, "users", callerUid));
    const callerProfile = callerSnap.exists() ? callerSnap.data() : {};
    const callerRole = normalizeRewardRole(callerProfile.user_role);
    const canViewOther = callerRole === "admin";
    if (requestedUid !== callerUid && !canViewOther) return () => { };

    const weekKey = currentIsoWeekKey();
    const unsubscribers = [];
    let timer = null;
    let stopped = false;

    const schedule = () => {
        if (stopped) return;
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(() => {
            timer = null;
            if (!stopped) onChange?.();
        }, 90);
    };
    const watch = (target) => {
        unsubscribers.push(onSnapshot(target, schedule, (error) => {
            if (!stopped) onError?.(error);
        }));
    };

    watch(doc(db, "users", callerUid));
    if (requestedUid !== callerUid) watch(doc(db, "users", requestedUid));
    watch(doc(db, memberPath(requestedUid)));
    watch(doc(db, memberCollectionPath(requestedUid, "periods"), weekKey));
    watch(query(collection(db, memberCollectionPath(requestedUid, "ledger")), orderBy("created_at", "desc"), limit(HISTORY_PAGE_SIZE)));
    watch(query(collection(db, memberCollectionPath(requestedUid, "achievements")), orderBy("awarded_at", "desc"), limit(100)));
    watch(query(collection(db, "rewards/catalog/items"), orderBy("sort_order", "asc"), limit(100)));
    watch(query(collection(db, memberCollectionPath(requestedUid, "redemptions")), orderBy("requested_at", "desc"), limit(50)));

    if (callerRole === "admin" && includeAdmin) {
        watch(query(collection(db, "rewards/admin/redemptions"), orderBy("requested_at", "desc"), limit(150)));
        watch(query(collection(db, "rewards/admin/quality"), orderBy("created_at", "desc"), limit(200)));
        watch(query(collection(db, "rewards/admin/incidents"), orderBy("occurred_at", "desc"), limit(50)));
        watch(query(collection(db, "rewards/admin/awards"), orderBy("approved_at", "desc"), limit(50)));
        watch(query(collection(db, "rewards/admin/audit"), orderBy("occurred_at", "desc"), limit(200)));
        watch(collection(db, "users"));
        watch(collection(db, "rewards/users/members"));
    }

    return () => {
        stopped = true;
        if (timer) window.clearTimeout(timer);
        unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
};

export const useRewardDashboard = (userId, enabled = true, options = {}) => {
    const includeAdmin = options?.includeAdmin !== false;
    const [state, setState] = useState({ data: null, loading: Boolean(enabled), error: "" });
    const refresh = useCallback(async ({ silent = false } = {}) => {
        if (!enabled || !userId) return;
        if (!silent) setState((current) => ({ ...current, loading: true, error: "" }));
        try {
            const data = await loadRewardDashboard(userId, { includeAdmin });
            setState({ data, loading: false, error: "" });
        } catch (error) {
            setState((current) => ({ ...current, loading: false, error: error?.message || "Could not load rewards." }));
        }
    }, [enabled, userId, includeAdmin]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    useEffect(() => {
        if (!enabled || !userId) return undefined;
        let disposed = false;
        let stop = () => { };
        void watchRewardDashboard(
            userId,
            () => { if (!disposed) void refresh({ silent: true }); },
            () => { },
            { includeAdmin },
        ).then((unsubscribe) => {
            if (disposed) unsubscribe();
            else stop = unsubscribe;
        }).catch(() => { });
        return () => {
            disposed = true;
            stop();
        };
    }, [enabled, userId, includeAdmin, refresh]);

    return { ...state, refresh };
};

export const useTicketRewardContext = (ticketId, enabled = true) => {
    const [state, setState] = useState({ data: null, loading: Boolean(enabled && ticketId), error: "" });

    const normalize = useCallback((snapshot) => {
        const raw = snapshot.exists() ? snapshot.data() : null;
        return raw ? {
            ticketId,
            workUnitId: raw.current_work_unit_id || null,
            status: raw.current_work_unit_status || null,
            currentAssigneeId: raw.current_assignee_id || null,
            currentWorkflowStage: raw.current_workflow_stage || null,
            flags: raw.current_flags || {
                difficult: { enabled: false },
                urgent_management_request: { enabled: false },
            },
        } : null;
    }, [ticketId]);

    const refresh = useCallback(async () => {
        if (!enabled || !ticketId) {
            setState({ data: null, loading: false, error: "" });
            return null;
        }
        try {
            const snapshot = await getDoc(doc(db, rewardTicketPath(ticketId)));
            const data = normalize(snapshot);
            setState({ data, loading: false, error: "" });
            return data;
        } catch (error) {
            setState({ data: null, loading: false, error: error?.message || "Could not load ticket rewards." });
            return null;
        }
    }, [enabled, ticketId, normalize]);

    useEffect(() => {
        if (!enabled || !ticketId) {
            setState({ data: null, loading: false, error: "" });
            return undefined;
        }
        setState((current) => ({ ...current, loading: true, error: "" }));
        return onSnapshot(
            doc(db, rewardTicketPath(ticketId)),
            (snapshot) => setState({ data: normalize(snapshot), loading: false, error: "" }),
            (error) => setState({ data: null, loading: false, error: error?.message || "Could not load ticket rewards." }),
        );
    }, [enabled, ticketId, normalize]);

    return { ...state, refresh };
};


export const useTicketRecordedActiveSeconds = (ticket, userId) => {
    const ticketId = ticket?.id || null;
    const [closedSeconds, setClosedSeconds] = useState(0);
    const [now, setNow] = useState(() => Date.now());

    const assignmentStart = useMemo(() => {
        const history = Array.isArray(ticket?.assignment_history) ? ticket.assignment_history : [];
        const matching = [...history].reverse().find((entry) => entry?.assignee_id === userId && !entry?.unassigned_at)
            || [...history].reverse().find((entry) => entry?.assignee_id === userId);
        return toDate(matching?.assigned_at)?.getTime() || 0;
    }, [ticket?.assignment_history, userId]);

    useEffect(() => {
        if (!ticketId || !userId) {
            setClosedSeconds(0);
            return undefined;
        }
        return onSnapshot(
            collection(db, "tickets", ticketId, "workSessions"),
            (snapshot) => {
                const total = snapshot.docs.reduce((sum, document) => {
                    const data = document.data() || {};
                    if (data.user_id !== userId) return sum;
                    if (ticket?.status === "active" && ticket?.currentSessionId && document.id === ticket.currentSessionId) return sum;
                    const started = toDate(data.started_at)?.getTime() || 0;
                    if (assignmentStart && started && started < assignmentStart) return sum;
                    const duration = Number(data.duration_seconds || 0);
                    return sum + (Number.isFinite(duration) && duration > 0 ? Math.floor(duration) : 0);
                }, 0);
                setClosedSeconds(total);
            },
            () => setClosedSeconds(0),
        );
    }, [ticketId, userId, ticket?.status, ticket?.currentSessionId, assignmentStart]);

    useEffect(() => {
        if (ticket?.status !== "active" || !ticket?.activeAt) return undefined;
        setNow(Date.now());
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [ticket?.status, ticket?.activeAt]);

    const activeStarted = toDate(ticket?.activeAt)?.getTime() || 0;
    const liveSeconds = ticket?.status === "active" && activeStarted
        ? Math.max(0, Math.floor((now - activeStarted) / 1000))
        : 0;
    return Math.max(0, closedSeconds + liveSeconds);
};

export const useTicketRewardEntries = (ticketId, userId) => {
    const [entries, setEntries] = useState([]);
    useEffect(() => {
        if (!ticketId || !userId) {
            setEntries([]);
            return undefined;
        }
        const ledgerQuery = query(
            collection(db, memberCollectionPath(userId, "ledger")),
            orderBy("created_at", "desc"),
            limit(150),
        );
        return onSnapshot(
            ledgerQuery,
            (snapshot) => {
                const nextEntries = snapshot.docs
                    .map((document) => ({ id: document.id, ...document.data() }))
                    .filter((entry) => entry.ticket_id === ticketId);
                setEntries(nextEntries);
                if (nextEntries.some((entry) => entry.status === "pending" && AUTO_CONFIRM_REWARD_TYPES.has(entry.type))) {
                    void callRewardFunction("reconcileAutomaticRewards", { userId }, false).catch(() => { });
                }
            },
            () => setEntries([]),
        );
    }, [ticketId, userId]);
    return entries;
};

export const reconcileAutomaticRewards = (userId) => callRewardFunction("reconcileAutomaticRewards", { userId }, false);

export const classifyTicketReward = ({
    ticketId,
    userId,
    difficult,
    urgentManagementRequest,
    reason,
}) => callRewardFunction("setRewardClassification", {
    ticketId,
    userId,
    difficult,
    urgentManagementRequest,
    reason,
});


export const searchRewardTickets = async (term) => {
    const value = String(term || "").trim();
    if (value.length < 2) return [];

    const rawIdPrefix = value.replace(/^#/, "");
    const normalized = rawIdPrefix.toLowerCase();
    const [recentSnap, idPrefixSnap] = await Promise.all([
        getDocs(query(collection(db, "tickets"), orderBy("created_at", "desc"), limit(500))),
        getDocs(query(
            collection(db, "tickets"),
            orderBy(documentId()),
            startAt(rawIdPrefix),
            endAt(`${rawIdPrefix}\uf8ff`),
            limit(20),
        )),
    ]);

    const uniqueDocs = new Map();
    [...idPrefixSnap.docs, ...recentSnap.docs].forEach((snapshot) => uniqueDocs.set(snapshot.id, snapshot));

    return [...uniqueDocs.values()]
        .map((snapshot) => {
            const data = snapshot.data() || {};
            const title = String(data.title || data.text || "(Untitled)");
            const description = String(data.description || "");
            const shortId = snapshot.id.slice(0, 6);
            const haystack = `${snapshot.id} ${shortId} ${title} ${description}`.toLowerCase();
            return { snapshot, data, title, shortId, haystack };
        })
        .filter((item) => item.haystack.includes(normalized))
        .sort((a, b) => {
            const aExact = a.snapshot.id.toLowerCase() === normalized || a.shortId.toLowerCase() === normalized ? 1 : 0;
            const bExact = b.snapshot.id.toLowerCase() === normalized || b.shortId.toLowerCase() === normalized ? 1 : 0;
            if (aExact !== bExact) return bExact - aExact;
            const aTime = toDate(a.data.updated_at || a.data.created_at)?.getTime() || 0;
            const bTime = toDate(b.data.updated_at || b.data.created_at)?.getTime() || 0;
            return bTime - aTime;
        })
        .slice(0, 12)
        .map(({ snapshot, data, title, shortId }) => ({
            id: snapshot.id,
            short_id: `#${shortId}`,
            title,
            status: data.status || null,
            assignee_id: data.assignee_id || data.assigneeId || null,
            workflow_stage: data.workflow_stage || null,
            completed_at: data.completed_at || null,
            updated_at: data.updated_at || data.created_at || null,
        }));
};

export const resolveRewardWorkUnit = async (ticketId, { userId = null, requireCompleted = false } = {}) => {
    const id = String(ticketId || "").trim();
    if (!id) return null;

    const ticketRewardSnapshot = await getDoc(doc(db, rewardTicketPath(id)));
    const currentId = ticketRewardSnapshot.exists() ? ticketRewardSnapshot.data()?.current_work_unit_id : null;

    const matches = (data) => {
        if (!data) return false;
        if (userId && data.user_id !== userId) return false;
        if (requireCompleted && data.status !== "completed") return false;
        return true;
    };

    if (currentId) {
        const currentSnapshot = await getDoc(doc(db, rewardTicketPath(id), "workUnits", currentId));
        if (currentSnapshot.exists() && matches(currentSnapshot.data())) {
            return { id: currentSnapshot.id, ...currentSnapshot.data() };
        }
    }

    const unitsSnapshot = await getDocs(collection(db, rewardTicketPath(id), "workUnits"));
    return unitsSnapshot.docs
        .map((snapshot) => ({ id: snapshot.id, ...snapshot.data() }))
        .filter(matches)
        .sort((a, b) => {
            const aTime = toDate(a.completed_at || a.updated_at || a.created_at)?.getTime() || 0;
            const bTime = toDate(b.completed_at || b.updated_at || b.created_at)?.getTime() || 0;
            return bTime - aTime;
        })[0] || null;
};

export const loadRewardHistoryPage = async (userId, cursor = null, pageSize = HISTORY_PAGE_SIZE) => {
    if (!userId) return { items: [], cursor: null, hasMore: false };
    const safePageSize = Math.max(20, Math.min(200, Number(pageSize || HISTORY_PAGE_SIZE)));
    const base = [
        collection(db, memberCollectionPath(userId, "ledger")),
        orderBy("created_at", "desc"),
    ];
    const historyQuery = cursor
        ? query(base[0], base[1], startAfter(cursor), limit(safePageSize))
        : query(base[0], base[1], limit(safePageSize));
    const snapshot = await getDocs(historyQuery);
    return {
        items: docsToData(snapshot),
        cursor: snapshot.docs[snapshot.docs.length - 1] || null,
        hasMore: snapshot.size === safePageSize,
    };
};


export const loadTicketRewardBreakdown = async (userId, ticketId) => {
    const uid = String(userId || "").trim();
    const id = String(ticketId || "").trim();
    if (!uid || !id) return { entries: [], awardedPoints: 0, deductedPoints: 0, netConfirmedPoints: 0, pendingPoints: 0 };

    const snapshot = await getDocs(query(
        collection(db, memberCollectionPath(uid, "ledger")),
        where("ticket_id", "==", id),
        limit(500),
    ));
    const entries = docsToData(snapshot).sort((a, b) => {
        const aTime = toDate(a.created_at || a.earned_at)?.getTime() || 0;
        const bTime = toDate(b.created_at || b.earned_at)?.getTime() || 0;
        return bTime - aTime;
    });
    const confirmed = entries.filter((entry) => (entry.status || "confirmed") === "confirmed");
    const awardedPoints = confirmed.reduce((sum, entry) => sum + Math.max(0, Number(entry.points || 0)), 0);
    const deductedPoints = Math.abs(confirmed.reduce((sum, entry) => sum + Math.min(0, Number(entry.points || 0)), 0));
    const netConfirmedPoints = confirmed.reduce((sum, entry) => sum + Number(entry.points || 0), 0);
    const pendingPoints = entries
        .filter((entry) => entry.status === "pending")
        .reduce((sum, entry) => sum + Math.max(0, Number(entry.points || 0)), 0);
    return { entries, awardedPoints, deductedPoints, netConfirmedPoints, pendingPoints };
};

export const loadPendingQualityReviewContext = async (ticketId, userId, workUnitId) => {
    if (!userId || !workUnitId) return { rewards: [], classification_details: {} };

    const [ledgerSnapshot, workUnitSnapshot] = await Promise.all([
        getDocs(query(
            collection(db, memberCollectionPath(userId, "ledger")),
            orderBy("created_at", "desc"),
            limit(250),
        )),
        ticketId
            ? getDoc(doc(db, rewardTicketPath(ticketId), "workUnits", workUnitId))
            : Promise.resolve(null),
    ]);

    const rewards = docsToData(ledgerSnapshot)
        .filter((entry) => entry.work_unit_id === workUnitId && entry.status === "pending" && QUALITY_REWARD_TYPES.has(entry.type))
        .map((entry) => ({
            type: entry.type,
            points: Number(entry.points || QUALITY_REWARD_POINTS[entry.type] || 0),
            ledger_id: entry.id,
            active_seconds: Number(entry?.metadata?.active_seconds || 0),
        }));

    const workUnit = workUnitSnapshot?.exists?.() ? (workUnitSnapshot.data() || {}) : {};
    const flags = workUnit.reward_flags || {};
    const classificationDetails = {
        difficult_task: {
            reason: String(flags?.difficult?.reason || "").trim(),
            classified_by: flags?.difficult?.approved_by || null,
            classified_at: flags?.difficult?.approved_at || null,
        },
        urgent_management_request: {
            reason: String(flags?.urgent_management_request?.reason || "").trim(),
            classified_by: flags?.urgent_management_request?.approved_by || null,
            classified_at: flags?.urgent_management_request?.approved_at || null,
        },
    };

    return { rewards, classification_details: classificationDetails };
};

export const loadPendingQualityRewards = async (userId, workUnitId) => {
    const context = await loadPendingQualityReviewContext(null, userId, workUnitId);
    return context.rewards;
};

export const requestRedemption = (itemId, note = "") => callRewardFunction("requestRewardRedemption", { itemId, note });
export const cancelRedemption = (redemptionId) => callRewardFunction("cancelRewardRedemption", { redemptionId });
export const reviewRedemption = (redemptionId, decision, reviewNote) => callRewardFunction("reviewRewardRedemption", {
    redemptionId,
    decision,
    reviewNote,
});
export const reviewTicketQuality = (ticketId, workUnitId, decisions, reason = "") => callRewardFunction("reviewTicketQuality", {
    ticketId,
    workUnitId,
    decisions: decisions || {},
    reason,
});
export const recordRewardIncident = (payload) => callRewardFunction("recordRewardIncident", payload);
export const applyAvoidableReworkPenalty = (payload) => callRewardFunction("applyAvoidableReworkPenalty", payload);
export const createManualAdjustment = (payload) => callRewardFunction("createManualRewardAdjustment", payload);
export const awardEmployeeOfMonth = (payload) => callRewardFunction("awardEmployeeOfMonth", payload);
